import moment from 'moment';
import { toast } from 'react-hot-toast';
import { LTL_TRANSPORT_MODES, TL_TRANSPORT_MODES, TRANSPORT_MODES } from './constants';

/**
 * Checks if an array is valid (not null, undefined, or empty).
 * @param {array} arr - The array to check.
 * @returns {boolean} True if the array is valid, false otherwise.
 */
export const isArrayLength = arr => {
  // Check if the input parameter is an array and has a length greater than zero.
  return Array.isArray(arr) && (arr.length > 0 ?? false);
};

// Return captilized string
export const capitalizeFirstLetter = str => {
  if (typeof str !== 'string') {
    return '';
  }
  const string = str && str.toString();
  return string ? string.charAt(0).toUpperCase() + string.slice(1) : null;
};

export const sortCountriesWithPriority = countriesArray => {
  // Priority list of country codes
  const priorityCodes = ['US', 'GB'];

  // Splitting the array into priority and normal countries
  const priorityCountries = countriesArray.filter(country => priorityCodes.includes(country.code));
  const otherCountries = countriesArray.filter(country => !priorityCodes.includes(country.code));

  // Optional: Sort otherCountries alphabetically by label if needed
  otherCountries.sort((a, b) => a.label?.localeCompare(b.label));

  // Concatenating the priority countries with the sorted other countries
  return [...priorityCountries, ...otherCountries];
};

export const showToaster = (type, message, options = {}) => {
  const defaultOptions = {
    position: 'top-center',
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'light',
    ...options, // Merge any additional options passed to the function
  };

  // Display the toast based on the type
  if (type === 'success') {
    toast.success(message, defaultOptions);
  } else if (type === 'error') {
    toast.error(message, defaultOptions);
  } else {
    console.warn('showToast called with invalid type. Expected "success" or "error".');
  }
};

//This is a sharetribe response import with data.data
export const getPublicDataAttributeValue = (attributeName, data) => {
  if (data && data.attributes && data.attributes.publicData) {
    const publicData = data.attributes.publicData;
    if (publicData.hasOwnProperty(attributeName)) {
      return publicData[attributeName];
    }
  }
  return '';
};

export const getProductImages = images => {
  if (!Array.isArray(images)) {
    return [];
  }
  return images
    .map(
      item =>
        item?.attributes?.variants &&
        (item?.attributes?.variants['scaled-medium']?.url ||
          item?.attributes?.variants['listing-card-2x']?.url)
    )
    .filter(Boolean);
};

/**
 * Returns an array of wishlist items associated with a user's profile.
 * @param {object} currentUser - The user object.
 * @returns {array} An array of wishlist items, or an empty array if input is invalid or wishlist is missing.
 */
export const getWishlist = currentUser => {
  return (
    (currentUser && currentUser.id && currentUser?.attributes?.profile?.publicData?.wishlist) || []
  );
};

export const copyToClipboard = async text => {
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.focus({
      preventScroll: true,
    });
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  } catch (e) {
    console.error(`Copying failed with error: ${e}`);
    throw e;
  }
};

export const isAuthPages = pathname => {
  return ['/signup', '/login', '/recover-password', '/confirm'].includes(pathname);
};

// Generate hours in 24-hour format
export const generateHours = () => {
  const options = [];
  for (let i = 0; i < 24; i++) {
    const hourValue = i.toString().padStart(2, '0');
    const hourOption = {
      value: hourValue,
      label: `${hourValue}:00`,
    };
    options.push(hourOption);
  }
  return options;
};

export const identity = v => v;

export const getUpdatedShippingDeadline = dateString => {
  const date = new Date(dateString);
  const day = date.getDay();
  let daysToAdd;

  switch (day) {
    case 1: // Monday
    case 3: // Wednesday
      daysToAdd = 9;
      break;
    case 2: // Tuesday
    case 4: // Thursday
    case 5: // Friday
      daysToAdd = 10;
      break;
    case 6: // Saturday
    case 0: // Sunday
      daysToAdd = 11;
      break;
    default:
      daysToAdd = 0;
  }

  date.setDate(date.getDate() + daysToAdd);
  return moment(date).format('dddd, MMMM D, YYYY');
};

export const calculateListingPrice = data => {
  let basePrice = 0;

  // Determine base price
  if (
    data.listing_media_types.includes('book') &&
    !data.listing_media_types.some(type => ['cd', 'dvd', 'blu-ray'].includes(type))
  ) {
    basePrice = 150;
  } else if (data.listing_media_types.some(type => ['cd', 'dvd', 'blu-ray'].includes(type))) {
    basePrice = 300;
  }

  // Apply reductions
  if (
    data.item_source.includes('book_sale_remainders') ||
    data.item_source.includes('book_store_remainders')
  ) {
    basePrice *= 0.8; // reduce by 20%
  }

  if (data.inventory_prepped === 'pre-sorted') {
    basePrice *= 0.7; // reduce by 30%
  }

  if (data.grading_status === 'not_graded') {
    basePrice *= 0.9; // reduce by 10%
  }

  // Apply auction reduction if applicable
  if (data.listingType === 'auction') {
    basePrice *= 0.8; // reduce by 20%
  }

  return basePrice.toFixed(2);
};

// Helper function to calculate the expiration timestamp
export const getExpirationTimestamp = days =>
  moment()
    .add(days, 'days')
    .unix();

export const calculatePercentage = (baseValue, percentage) => {
  if (typeof baseValue !== 'number' || typeof percentage !== 'number') {
    throw new TypeError('Both baseValue and percentage must be numbers');
  }
};

export const getTxImage = tx => {
  const currentListing = tx?.listing;
  const firstImage =
    currentListing && currentListing.images && currentListing.images.length > 0
      ? currentListing.images[0]
      : null;

  const sharetribeImage =
    firstImage &&
    firstImage.attributes &&
    firstImage.attributes.variants &&
    firstImage.attributes.variants['square-small2x'] &&
    firstImage.attributes.variants['square-small2x'].url;

  return sharetribeImage;
};

export const getTxTitle = tx => {
  const currentListing = tx?.listing;
  const title = currentListing && currentListing.attributes && currentListing.attributes.title;
  return title;
};

export const allCounterCyclesCompleted = tx => {
  const transitions = tx?.attributes?.transitions;
  return (
    isArrayLength(transitions) &&
    transitions.filter(t => t.transition.includes('counter')).length === 4
  );
};

export const getDisplayName = currentUser => currentUser?.attributes?.profile?.displayName || '';

export const getOfferPrice = tx => {
  return tx && tx.id && tx?.attributes?.protectedData?.offerParams?.offerPrice;
};

export const getOfferParams = tx => {
  return tx && tx.id && tx?.attributes?.protectedData?.offerParams;
};

export const isOwnOffer = (customer, currentUser) => {
  return customer?.id?.uuid === currentUser?.id?.uuid;
};

export const hasOfferExpired = createdAt => {
  const expirationDate = moment(createdAt).add(48, 'hours');
  const timeRemaining = moment.duration(expirationDate.diff(moment())).asHours();
  return timeRemaining <= 0;
};

export const maskUserFullName = (user, maskChar = '*') => {
  if (!user?.id) return null;

  const { firstName, lastName } = user?.attributes?.profile || {};

  if (!firstName || !lastName) return null;

  const maskedLastName = lastName[0] + maskChar.repeat(lastName.length - 1);

  return `${firstName} ${maskedLastName}`;
};

export function processHeading(heading) {
  if (!heading) return null;
  const croppedHeadings = heading.replace(/select all/i, '').trim();
  const capitalizedHeading = croppedHeadings?.charAt(0)?.toUpperCase() + croppedHeadings?.slice(1);
  return capitalizedHeading;
}

export function maskName(name) {
  if (!name) return '';
  return name
    .split(' ')
    .map(word => word[0] + '*'.repeat(word.length - 1))
    .join(' ');
}

export const formatUnixToCustomDate = unixTimestamp => {
  const date = new Date(unixTimestamp * 1000); // Convert to milliseconds
  const options = {
    weekday: 'long', // "Tuesday"
    month: 'numeric', // "7"
    day: 'numeric', // "30"
    year: '2-digit', // "24"
    hour: 'numeric', // "12"
    minute: '2-digit', // "15"
    hour12: true, // "PM"
  };

  return `Ends ${date.toLocaleDateString('en-US', options)}`;
};

export const getTransportModes = pallets => {
  if (pallets > 6) {
    return TL_TRANSPORT_MODES;
  } else {
    return LTL_TRANSPORT_MODES;
  }
};
